import {
    useMemo,
} from 'react'
import SkyNetTable from 'app/shared-components/SkyNetTable'
import SkyNetDomain, {
    SidebarTab,
} from 'app/shared-components/SkyNetDomain'
import useHasPermission from 'app/hooks/useHasPermission'
import {
    TableControlTypes,
} from 'app/shared-components/TableControls'
import useTabsByPermission from 'app/hooks/useTabsByPermission'
import {
    CommonRoutes,
} from 'app/enums/common.enums'
import {
    AccountTabs,
} from 'app/enums/domain.enums'
import useFetchQuerySelector from 'app/hooks/useFetchQuerySelector'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    TableResponse,
} from 'app/types/request.types'
import requests from 'app/requests/Account/Account.requests'
import aQuotesRequest from 'app/requests/Quote/Quote.requests'
import cQuotesRequest from 'app/requests/CQuote/CQuote.requests'
import {
    AQuoteTable,
} from '../AQuotes/AQuotes.types'
import {
    CQuotesTable,
} from '../CQuotes/CQuotes.types'
import AccountOverview from './AccountOverview'
import AccountUpdate from './AccountUpdate'
import tableConfig from './Accounts.config'
import CreateAccount from './AccountCreate'
import Contacts from './Contacts'
import LanesByAccount from './LanesByAccount'
import AQuotesByAccount from './AQuotesByAccount'
import {
    BillingCompaniesByAccount,
} from './BillingCompaniesByAccountCompany'
import {
    AddressesByAccount,
} from './AddressesByAccountCompany'
import {
    ActivityRecordsByAccount,
} from './ActivityRecordsByAccountCompany'
import CQuotesByAccount from './CQuotesByAccount'

const tableControls: TableControlTypes[] = [
    TableControlTypes.ShowArhived,
    TableControlTypes.ClearFilters,
    TableControlTypes.ClearSorting,
]

const Accounts = () => {
    const accountCreate = useHasPermission(['account_create_all'])
    const accountUpdate = useHasPermission(['account_update_all'])

    const fetchAQuotes = useFetchQuerySelector<TableResponse<AQuoteTable[]>>({
        key: RequestKeys.getAQuotes,
        selectorConfig: aQuotesRequest.getAQuotes,
    })

    const fetchCQuotes = useFetchQuerySelector<TableResponse<CQuotesTable[]>>({
        key: RequestKeys.cQuotesFilterRequest,
        selectorConfig: cQuotesRequest.cQuotesFilterRequest,
    })

    const tabs: SidebarTab[] = useTabsByPermission([
        {
            tab: {
                label: 'Overview',
                url: CommonRoutes.Overview,
                Component: AccountOverview,
            },
            permission: true,
        },
        {
            tab: {
                label: 'Data Update',
                url: CommonRoutes.DataUpdate,
                Component: AccountUpdate,
            },
            permission: accountUpdate,
        },
        {
            tab: {
                label: 'Lanes',
                url: AccountTabs.Lanes,
                Component: LanesByAccount,
            },
            permission: accountUpdate,
        },
        {
            tab: {
                label: 'A-Quotes',
                url: AccountTabs.AQuotes,
                Component: AQuotesByAccount,
                condition: async ({
                    id,
                } = {}) => {
                    if (id) {
                        const response = await fetchAQuotes({
                            params: {
                                rows: 1,
                                includeFilters: {
                                    accountId: [id],
                                },
                            },
                        })

                        return !!response?.data?.length
                    }
                    return false
                },
            },
            permission: accountUpdate,
        },
        {
            tab: {
                label: 'C-Quotes',
                url: AccountTabs.CQuotes,
                Component: CQuotesByAccount,
                condition: async ({
                    id,
                } = {}) => {
                    if (id) {
                        const response = await fetchCQuotes({
                            params: {
                                rows: 1,
                                includeFilters: {
                                    pricingAccountId: [id],
                                },
                            },
                        })

                        return !!response?.data?.length
                    }
                    return false
                },
            },
            permission: accountUpdate,
        },
        {
            tab: {
                label: 'Billing Companies',
                url: AccountTabs.BillingCompanies,
                Component: BillingCompaniesByAccount,
            },
            permission: accountUpdate,
        },
        {
            tab: {
                label: 'Addresses',
                url: AccountTabs.Addresses,
                Component: AddressesByAccount,
            },
            permission: accountUpdate,
        },
        {
            tab: {
                label: 'Contacts',
                url: AccountTabs.Contacts,
                Component: Contacts,
            },
            permission: true,
        },
        {
            tab: {
                label: 'Activity Records',
                url: AccountTabs.ActivityRecords,
                Component: ActivityRecordsByAccount,
            },
            permission: accountUpdate,
        },
    ])

    const actionComponents = useMemo(() => {
        return {
            Create: accountCreate ? CreateAccount : undefined,
        }
    }, [accountCreate])

    return (
        <SkyNetDomain
            getDomainObject={requests.get}
            domainRequestKey={RequestKeys.getAccount}
            actionComponents={actionComponents}
            name={tableConfig.name}
            defaultTab="overview"
            sidebarTabs={tabs}
        >
            <SkyNetTable
                tableControls={tableControls}
                tableConfig={tableConfig}
                showSearch
            />
        </SkyNetDomain>
    )
}

export default Accounts
